import type { ByecodeSize } from '@byecode/ui'
import { Flex } from '@byecode/ui'
import type { DataSourceAbstract } from '@lighthouse/core'
import { type Field, type VariableADTvalue, VariableType } from '@lighthouse/core'
import cls from 'classnames'
import React, { useMemo } from 'react'

import type { VariableTree2 } from '../../types'
import { VariableTag } from '../../VariableTag'
import { VariablePreview } from '../Preview'
import * as SC from './styles'

interface VariableInputProps {
    size?: ByecodeSize

    value?: VariableADTvalue
    placeholder?: string
    field?: Field
    width?: string
    disabled?: boolean

    pageOptions?: VariableTree2[]
    upstreamOptions?: VariableTree2[]
    userOption?: VariableTree2
    datasourceOption?: VariableTree2
    datasourceOptions?: VariableTree2[]
    inputOption?: VariableTree2
    filterOption?: VariableTree2
    formOption?: VariableTree2
    eventOption?: VariableTree2
    loopOption?: VariableTree2
    viewOptions?: VariableTree2[]
    imageOption?: VariableTree2

    opened?: boolean
    filePercent?: number
    dataSourceList?: DataSourceAbstract[]
    prefix?: React.ReactNode
    wrapperStyle?: React.CSSProperties
    focusOutLine?: boolean
    onChange: (value: VariableADTvalue) => void
    onChangeOpen: () => void
    onOpenSelectDataSourceModal: () => void
    onCancelUpload: () => void
}

export const VariableInput: React.FunctionComponent<VariableInputProps> = ({
    size,
    value: data,
    placeholder,
    field,
    disabled,
    width = '180px',

    pageOptions,
    upstreamOptions,
    userOption,
    datasourceOption,
    datasourceOptions,
    inputOption,
    filterOption,
    formOption,
    viewOptions,
    eventOption,
    loopOption,
    imageOption,

    opened,
    filePercent,
    dataSourceList,
    prefix,
    wrapperStyle,
    focusOutLine,
    onCancelUpload,
    onOpenSelectDataSourceModal,
    onChangeOpen,
    onChange
}) => {
    const variableEle = useMemo(() => {
        if (!data || data?.type === VariableType.VALUE) {
            return (
                <VariablePreview
                    size={size}
                    width={width}
                    placeholder={placeholder}
                    value={data}
                    disabled={disabled}
                    field={field}
                    opened={opened}
                    filePercent={filePercent}
                    prefix={prefix}
                    focusOutLine={focusOutLine}
                    onChangeOpen={onChangeOpen}
                    onChange={onChange}
                    onCancelUpload={onCancelUpload}
                />
            )
        }
        return (
            <SC.TagContainer onClick={() => onChangeOpen?.()}>
                <SC.TagWrapper>
                    <VariableTag
                        value={data}
                        pageOptions={pageOptions}
                        upstreamOptions={upstreamOptions}
                        userOption={userOption}
                        datasourceOption={datasourceOption}
                        datasourceOptions={datasourceOptions}
                        inputOption={inputOption}
                        filterOption={filterOption}
                        formOption={formOption}
                        viewOptions={viewOptions}
                        eventOption={eventOption}
                        loopOption={loopOption}
                        imageOption={imageOption}
                        dataSourceList={dataSourceList}
                        style={{
                            lineHeight: '24px'
                        }}
                        onOpenSelectDataSourceModal={onOpenSelectDataSourceModal}
                        onChangeVariableValue={onChange}
                    />
                </SC.TagWrapper>
                <Flex justifyContent="flex-end" alignItems="center" style={{ height: '100%' }}>
                    <SC.Icon
                        type="CloseCircle"
                        onClick={e => {
                            e.stopPropagation()
                            !disabled && onChange({ type: VariableType.VALUE })
                        }}
                    />
                    <SC.Icon type="ArrowDownSmall" />
                </Flex>
            </SC.TagContainer>
        )
    }, [
        data,
        pageOptions,
        upstreamOptions,
        userOption,
        datasourceOption,
        datasourceOptions,
        inputOption,
        filterOption,
        formOption,
        viewOptions,
        eventOption,
        loopOption,
        imageOption,
        dataSourceList,
        onOpenSelectDataSourceModal,
        onChange,
        size,
        width,
        placeholder,
        disabled,
        field,
        opened,
        filePercent,
        prefix,
        focusOutLine,
        onChangeOpen,
        onCancelUpload
    ])

    return (
        <SC.Container>
            <SC.Wrapper width={width} size={size} className={cls({ variableDisabled: disabled })} style={wrapperStyle}>
                {variableEle}
            </SC.Wrapper>
        </SC.Container>
    )
}
